'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth, CfvaRole } from '@/contexts/AuthContext';

export type TeamId = 'education' | 'production' | 'development' | 'leadership';

export interface Team {
  id: TeamId;
  label: string;
  shortLabel: string;
  description: string;
  color: string;
  bgColor: string;
  borderColor: string;
  accentBg: string;
  branches: ('live_events' | 'studio' | 'outreach' | 'internal')[];
  defaultView: string;
}

export const TEAMS: Team[] = [
  {
    id: 'education',
    label: 'Education',
    shortLabel: 'EDU',
    description: 'Studio, lessons & instructors',
    color: 'text-teal-700',
    bgColor: 'bg-teal-600',
    borderColor: 'border-teal-500',
    accentBg: 'bg-teal-50',
    branches: ['studio'],
    defaultView: 'today',
  },
  {
    id: 'production',
    label: 'Production',
    shortLabel: 'PRD',
    description: 'Live events, gigs & performances',
    color: 'text-indigo-700',
    bgColor: 'bg-indigo-600',
    borderColor: 'border-indigo-500',
    accentBg: 'bg-indigo-50',
    branches: ['live_events'],
    defaultView: 'today',
  },
  {
    id: 'development',
    label: 'Development',
    shortLabel: 'DEV',
    description: 'Fundraising, grants & outreach',
    color: 'text-amber-700',
    bgColor: 'bg-amber-500',
    borderColor: 'border-amber-400',
    accentBg: 'bg-amber-50',
    branches: ['outreach'],
    defaultView: 'today',
  },
  {
    id: 'leadership',
    label: 'Leadership',
    shortLabel: 'LDR',
    description: 'Cross-team visibility & admin',
    color: 'text-violet-700',
    bgColor: 'bg-violet-600',
    borderColor: 'border-violet-500',
    accentBg: 'bg-violet-50',
    branches: ['live_events', 'studio', 'outreach', 'internal'],
    defaultView: 'today',
  },
];

/** Map a Supabase role to the corresponding TeamId */
export function roleToTeamId(role: CfvaRole | null | undefined): TeamId {
  switch (role) {
    case 'education':   return 'education';
    case 'production':  return 'production';
    case 'development': return 'development';
    case 'leadership':  return 'leadership';
    default:            return 'leadership'; // fallback for unauthenticated / loading
  }
}

interface TeamContextValue {
  activeTeam: Team;
  /** Only effective for leadership users; ignored for other roles */
  setActiveTeamId: (id: TeamId) => void;
  /** Whether the current user can manually switch teams */
  canSwitchTeams: boolean;
}

const TeamContext = createContext<TeamContextValue>({
  activeTeam: TEAMS[3],
  setActiveTeamId: () => {},
  canSwitchTeams: false,
});

export function TeamProvider({ children }: { children: React.ReactNode }) {
  const { userProfile, loading } = useAuth();
  const [manualTeamId, setManualTeamId] = useState<TeamId | null>(null);

  // Derive the team from the user's role
  const roleTeamId = roleToTeamId(userProfile?.role);
  const isLeadership = roleTeamId === 'leadership';

  // Reset manual override when role changes
  useEffect(() => {
    setManualTeamId(null);
  }, [userProfile?.role]);

  // Leadership can override; everyone else is locked to their role
  const activeTeamId: TeamId = isLeadership && manualTeamId ? manualTeamId : roleTeamId;
  const activeTeam = TEAMS.find((t) => t.id === activeTeamId) ?? TEAMS[3];

  function setActiveTeamId(id: TeamId) {
    if (isLeadership) setManualTeamId(id);
  }

  // While auth is loading, default to leadership (shows all data)
  const resolvedTeam = loading ? TEAMS[3] : activeTeam;

  return (
    <TeamContext.Provider
      value={{
        activeTeam: resolvedTeam,
        setActiveTeamId,
        canSwitchTeams: isLeadership,
      }}
    >
      {children}
    </TeamContext.Provider>
  );
}

export function useTeam() {
  return useContext(TeamContext);
}
